import {
  Checkbox,
  HeaderCell,
  IconInfoOutline,
  InfoButton,
  TableHeader,
  Text,
} from '@bbl-digital/snorre'
import { useIsMobile } from '@bbl-digital/snorre-v2'
import { useTheme } from '@emotion/react'
import useRecipientsChecked from 'Modules/Communication/shared/hooks/useRecipientsChecked'
import {
  FilterTypeEnum,
  IRecipientQueryModel,
  RecipientsSortByEnum,
} from 'Modules/Communication/shared/models/Recipients'
import { FC, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'shared/store/store'
import { ISortOrderEnum } from 'shared/models/List'

import { setRecipientsFilters } from 'shared/store/sendMessage/recipients'
import { getMessageRecipients } from 'shared/store/sendMessage/recipients/selectors'
import { useTerminology } from 'shared/hooks/useTerminology'
import {
  CheckboxWrapper,
  InfoButtonWrapper,
  MobileCheckbox,
  MobileCheckboxWrapper,
} from './styles'

const RecipientsListHeader: FC = () => {
  const isMobile = useIsMobile()
  const { gray10 } = useTheme()

  const { filters, openState, meters, objects, data } =
    useAppSelector(getMessageRecipients)

  const { allRecipientsChecked, handleMultiCheckboxChange } =
    useRecipientsChecked(openState)

  const { Eiere, Enhet } = useTerminology()
  const dispatch = useAppDispatch()

  const [hasReservedRecipients, setHasReservedRecipients] = useState(false)

  useEffect(() => {
    if (
      data?.result?.some((item) => item.reservedAgainstElectronicCommunication)
    ) {
      setHasReservedRecipients(true) // Only set to true, never revert to false
    }
  }, [data])

  const sort = filters?.sortcolumn
  const sortOrder =
    (filters?.sortorder === ISortOrderEnum.asc && 'asc') ||
    (filters?.sortorder === ISortOrderEnum.desc && 'desc') ||
    'none'

  const sortingOrder = (field: string) => (sort === field ? sortOrder : 'none')

  const handleChange =
    (field: string, isDefault?: boolean) => (order: string) => {
      let orderBy = order

      if (isDefault) {
        orderBy =
          order || sortOrder === 'desc'
            ? ISortOrderEnum.asc
            : ISortOrderEnum.desc
      }

      const payload: IRecipientQueryModel = {
        ...filters,
        sortcolumn: orderBy ? field : 'name',
        sortorder: orderBy || ISortOrderEnum.asc,
        skip: 0,
      }

      dispatch(setRecipientsFilters(payload))
    }

  const isMeters = filters.filterType === FilterTypeEnum.Meters
  const isObjects = filters.filterType === FilterTypeEnum.Objects
  const isBoardAndCommitee = filters.filterType === FilterTypeEnum.BoardMembers
  const isOtherOrBoardContacts =
    filters.filterType === FilterTypeEnum.BoardContacts ||
    filters.filterType === FilterTypeEnum.OtherContacts
  // Check nameId on listedRecipients to see all of them exist in data.result
  const isAllChecked = allRecipientsChecked()

  const InfoButtonComponent = (
    <InfoButtonWrapper>
      <InfoButton icon={<IconInfoOutline primary />} place="right">
        Hvis du vil markere alle mottakerne i listen, må du justere antall rader
        hvis det er mer enn 50 mottakere. Hvis det er mer enn 100, må du krysse
        av for alle på hver side. Du kan endre antall rader nederst til venstre
        i dette vinduet.
      </InfoButton>
    </InfoButtonWrapper>
  )

  const CheckboxComponent = (
    <CheckboxWrapper key="header-checkbox">
      {!isMobile && InfoButtonComponent}
      <Checkbox
        checked={data?.result?.length ? isAllChecked : false}
        onChange={handleMultiCheckboxChange}
      />
    </CheckboxWrapper>
  )

  const NameComponent = (
    <HeaderCell
      key="header-name"
      sortable
      sortOrder={sortingOrder(RecipientsSortByEnum.Name)}
      onSortChange={handleChange(RecipientsSortByEnum.Name, true)}
      ascendingReturnValue={ISortOrderEnum.asc}
      descendingReturnValue={ISortOrderEnum.desc}
    >
      Navn
    </HeaderCell>
  )

  const OwnerComponent = (
    <HeaderCell
      key="header-owner"
      sortable
      sortOrder={sortingOrder(RecipientsSortByEnum.Name)}
      onSortChange={handleChange(RecipientsSortByEnum.Name, true)}
      ascendingReturnValue={ISortOrderEnum.asc}
      descendingReturnValue={ISortOrderEnum.desc}
    >
      {Eiere}
    </HeaderCell>
  )

  const PhoneComponent = (
    <HeaderCell
      key="header-mobile"
      sortable
      sortOrder={sortingOrder(RecipientsSortByEnum.Email)}
      onSortChange={handleChange(RecipientsSortByEnum.Email)}
      ascendingReturnValue={ISortOrderEnum.asc}
      descendingReturnValue={ISortOrderEnum.desc}
    >
      Mobil
    </HeaderCell>
  )

  const EmailComponent = (
    <HeaderCell
      key="header-email"
      sortable
      sortOrder={sortingOrder(RecipientsSortByEnum.Email)}
      onSortChange={handleChange(RecipientsSortByEnum.Email)}
      ascendingReturnValue={ISortOrderEnum.asc}
      descendingReturnValue={ISortOrderEnum.desc}
    >
      E-post
    </HeaderCell>
  )

  const ContactComponent =
    openState === 'Message'
      ? null
      : openState === 'SMS'
        ? PhoneComponent
        : EmailComponent

  const UnitPropertyComponent = (
    <HeaderCell
      key="header-unit-property"
      sortable
      ascendingReturnValue={ISortOrderEnum.asc}
      descendingReturnValue={ISortOrderEnum.desc}
      sortOrder={sortingOrder(RecipientsSortByEnum.UnitPropertyNo)}
      onSortChange={handleChange(RecipientsSortByEnum.UnitPropertyNo)}
    >
      {Enhet}
    </HeaderCell>
  )

  const BoardFunctionComponent = (
    <HeaderCell key="header-member-function">Styrefunksjon</HeaderCell>
  )

  const AddressComponent = (
    <HeaderCell
      key="header-address"
      sortable
      ascendingReturnValue={ISortOrderEnum.asc}
      descendingReturnValue={ISortOrderEnum.desc}
      sortOrder={sortingOrder(RecipientsSortByEnum.Address)}
      onSortChange={handleChange(RecipientsSortByEnum.Address)}
    >
      Adresse
    </HeaderCell>
  )

  const PositionComponent = (
    <HeaderCell
      key="header-position"
      sortable
      sortOrder={sortingOrder(RecipientsSortByEnum.Position)}
      onSortChange={handleChange(RecipientsSortByEnum.Position)}
      ascendingReturnValue={ISortOrderEnum.asc}
      descendingReturnValue={ISortOrderEnum.desc}
    >
      Stilling
    </HeaderCell>
  )

  const CompanyNameComponent = (
    <HeaderCell
      key="header-company-name"
      sortable
      sortOrder={sortingOrder(RecipientsSortByEnum.CompanyName)}
      onSortChange={handleChange(RecipientsSortByEnum.CompanyName)}
      ascendingReturnValue={ISortOrderEnum.asc}
      descendingReturnValue={ISortOrderEnum.desc}
    >
      Firma
    </HeaderCell>
  )

  const MeterComponent = (
    <HeaderCell key="header-meter">
      {meters?.find((item) => item.meterTypeId === filters.meterTypeNo)
        ?.meterType || 'Målere'}
    </HeaderCell>
  )

  const ObjectComponent = (
    <HeaderCell key="header-object">
      {objects?.find((item) => item.objectTypeId === filters.objectTypeNo)
        ?.objectType || 'Objekte'}
    </HeaderCell>
  )

  const ReservedComponent = hasReservedRecipients ? (
    <HeaderCell
      key="header-reserved"
      sortable={hasReservedRecipients}
      sortOrder={sortingOrder(RecipientsSortByEnum.Reservation)}
      onSortChange={handleChange(RecipientsSortByEnum.Reservation)}
      ascendingReturnValue={ISortOrderEnum.asc}
      descendingReturnValue={ISortOrderEnum.desc}
    >
      Reservert
    </HeaderCell>
  ) : (
    <HeaderCell key="header-reserved" />
  )

  const SSbnrHeader = (
    <HeaderCell
      key="ssbnr"
      sortable
      sortOrder={sortingOrder(RecipientsSortByEnum.UnitSsbNo)}
      onSortChange={handleChange(RecipientsSortByEnum.UnitSsbNo)}
      ascendingReturnValue={ISortOrderEnum.asc}
      descendingReturnValue={ISortOrderEnum.desc}
    >
      Bolignr
    </HeaderCell>
  )

  const isAlternativeName =
    isBoardAndCommitee || isOtherOrBoardContacts || isMeters || isObjects

  // Renders instead of the table header on mobile
  if (isMobile) {
    return (
      <MobileCheckboxWrapper>
        {InfoButtonComponent}
        <MobileCheckbox>
          {CheckboxComponent}
          <Text color={gray10} bold small>
            {isAlternativeName ? 'Navn' : Eiere}
          </Text>
        </MobileCheckbox>
      </MobileCheckboxWrapper>
    )
  }

  const getComponentList = () => {
    if (isBoardAndCommitee)
      return [
        CheckboxComponent,
        NameComponent,
        ContactComponent,
        BoardFunctionComponent,
      ]

    if (isOtherOrBoardContacts)
      return [
        CheckboxComponent,
        NameComponent,
        CompanyNameComponent,
        PositionComponent,
        ContactComponent,
      ]

    if (isMeters)
      return [
        CheckboxComponent,
        NameComponent,
        MeterComponent,
        ContactComponent,
      ]

    if (isObjects)
      return [
        CheckboxComponent,
        NameComponent,
        ObjectComponent,
        ContactComponent,
      ]

    return [
      CheckboxComponent,
      OwnerComponent,
      ContactComponent,
      UnitPropertyComponent,
      AddressComponent,
      SSbnrHeader,
      ReservedComponent,
    ]
  }

  // Owners or Tenants (default)
  return <TableHeader>{getComponentList()}</TableHeader>
}

export default RecipientsListHeader
